import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getCustomer,
  updateCustomer,
  archiveCustomer,
  deleteCustomer,
  getCustomerHistory,
  listLinks,
  createLink,
  revokeLinks,
  exportCustomerUrl,
  listQuestions,
  requestLogoUpload,
  uploadLogoFile,
  ApiError,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { Customer, QuestionHistory, LinkStatus, Question } from '../types';

export default function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin, accessToken } = useAuth();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [history, setHistory] = useState<QuestionHistory[]>([]);
  const [links, setLinks] = useState<LinkStatus[]>([]);
  const [allQuestions, setAllQuestions] = useState<Question[]>([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<Set<string> | null>(null);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newLinkDays, setNewLinkDays] = useState(14);
  const [newRawToken, setNewRawToken] = useState<string | null>(null);
  const [expandedHistory, setExpandedHistory] = useState<Set<string>>(new Set());
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [groupFilter, setGroupFilter] = useState<Set<string>>(new Set());

  function load() {
    if (!id) return;
    getCustomer(id)
      .then((res) => {
        setCustomer(res.customer);
        setSelectedQuestionIds(res.customer.assignedQuestionIds ? new Set(res.customer.assignedQuestionIds) : null);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load customer.'));
    getCustomerHistory(id)
      .then((res) => setHistory(res.questionHistories))
      .catch(() => undefined);
    listLinks(id)
      .then((res) => setLinks(res.links))
      .catch(() => undefined);
    listQuestions()
      .then((res) => setAllQuestions(res.questions.sort((a, b) => a.order - b.order)))
      .catch(() => undefined);
  }

  useEffect(load, [id]);

  function toggleQuestion(questionId: string) {
    setSelectedQuestionIds((prev) => {
      // First toggle from "all questions" (null) starts from the full set,
      // so unchecking one box narrows it down rather than clearing everything.
      const base = prev ?? new Set(allQuestions.map((q) => q.questionId));
      const next = new Set(base);
      if (next.has(questionId)) next.delete(questionId);
      else next.add(questionId);
      return next;
    });
  }

  async function handleSaveAssignment() {
    if (!id) return;
    setSavingAssignment(true);
    setError(null);
    try {
      const ids = selectedQuestionIds ? Array.from(selectedQuestionIds) : null;
      await updateCustomer(id, { assignedQuestionIds: ids });
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save question assignment.');
    } finally {
      setSavingAssignment(false);
    }
  }

  function handleResetToAllQuestions() {
    setSelectedQuestionIds(null);
    setGroupFilter(new Set());
  }

  function toggleGroupFilter(group: string) {
    setGroupFilter((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  }

  // All groups actually in use across the question bank, including any
  // custom group an admin typed in on the Questions page - so a brand-new
  // group shows up here exactly like the built-in ones.
  const availableGroups = Array.from(new Set(allQuestions.map((q) => q.group ?? 'AWS Migration'))).sort((a, b) =>
    a.localeCompare(b),
  );

  // Visible questions narrow to the selected groups (none selected = show all).
  const visibleQuestions = allQuestions.filter(
    (q) => !q.archived && (groupFilter.size === 0 || groupFilter.has(q.group ?? 'AWS Migration')),
  );

  function handleSelectAllVisible() {
    setSelectedQuestionIds((prev) => {
      const base = prev ?? new Set(allQuestions.map((q) => q.questionId));
      const next = new Set(base);
      visibleQuestions.forEach((q) => next.add(q.questionId));
      return next;
    });
  }

  function handleDeselectAllVisible() {
    setSelectedQuestionIds((prev) => {
      const base = prev ?? new Set(allQuestions.map((q) => q.questionId));
      const next = new Set(base);
      visibleQuestions.forEach((q) => next.delete(q.questionId));
      return next;
    });
  }

  async function handleArchive() {
    if (!id || !confirm('Archive this customer?')) return;
    await archiveCustomer(id);
    load();
  }

  async function handleDelete() {
    if (!id) return;
    if (!confirm('Permanently delete this customer and all their data? This cannot be undone.')) return;
    if (!confirm('This is irreversible. Type OK in the next prompt to confirm again.')) return;
    await deleteCustomer(id);
    navigate('/customers');
  }

  async function handleCreateLink() {
    if (!id) return;
    setError(null);
    try {
      const expiresAt = new Date(Date.now() + newLinkDays * 86_400_000).toISOString();
      const result = await createLink(id, expiresAt);
      setNewRawToken(result.token);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create link.');
    }
  }

  async function handleRevokeLinks() {
    if (!id || !confirm('Revoke all active links for this customer?')) return;
    await revokeLinks(id);
    load();
  }

  const ALLOWED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
  const MAX_LOGO_BYTES = 2 * 1024 * 1024;

  async function handleLogoFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!id || !file) return;

    setLogoError(null);
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      setLogoError('Logo must be a PNG, JPEG, WEBP, or SVG image.');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError('Logo must be 2 MB or smaller.');
      return;
    }

    setUploadingLogo(true);
    try {
      const { uploadUrl } = await requestLogoUpload(id, file.type, file.size);
      await uploadLogoFile(uploadUrl, file);
      load();
    } catch (err) {
      setLogoError(err instanceof ApiError ? err.message : 'Failed to upload logo.');
    } finally {
      setUploadingLogo(false);
    }
  }

  function toggleHistory(questionId: string) {
    setExpandedHistory((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) next.delete(questionId);
      else next.add(questionId);
      return next;
    });
  }

  if (error) return <div className="error-banner">{error}</div>;
  if (!customer) return <p>Loading...</p>;

  const publicUrl = newRawToken ? `${window.location.origin}/q/${newRawToken}` : null;

  return (
    <div>
      <h1>{customer.companyName}</h1>

      <div className="card">
        <h2>Profile</h2>
        <p>
          <strong>Contact:</strong> {customer.contactName} ({customer.contactEmail})
        </p>
        {customer.notes && <p className="muted">{customer.notes}</p>}
        <p>
          <strong>Status:</strong> {customer.status}
        </p>
        {isAdmin && (
          <div className="field">
            <label htmlFor="logo-upload">Customer logo (optional)</label>
            <p className="muted" style={{ marginTop: -4 }}>
              Shown on this customer's public questionnaire page in place of the Phoenix Consulting logo. PNG,
              JPEG, WEBP, or SVG, up to 2 MB.
            </p>
            <div className="row" style={{ alignItems: 'center' }}>
              {customer.logoUrl && (
                <img
                  src={customer.logoUrl}
                  alt={`${customer.companyName} logo`}
                  style={{ height: 40, maxWidth: 160, objectFit: 'contain', flex: 'none' }}
                />
              )}
              <input
                id="logo-upload"
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={handleLogoFileSelected}
                disabled={uploadingLogo}
                style={{ flex: 1 }}
              />
            </div>
            {uploadingLogo && <p className="muted">Uploading...</p>}
            {logoError && <p style={{ color: 'var(--color-danger)' }}>{logoError}</p>}
          </div>
        )}
        {isAdmin && (
          <div className="row">
            <button onClick={handleArchive}>Archive</button>
            <button className="danger" onClick={handleDelete}>
              Delete permanently
            </button>
            <button
              onClick={() =>
                downloadExport(exportCustomerUrl(customer.customerId), accessToken, `customer-${customer.customerId}-export.xlsx`)
              }
            >
              Export Excel
            </button>
          </div>
        )}
      </div>

      {isAdmin && (
        <div className="card">
          <h2>Assigned questions</h2>
          <p className="muted">
            {selectedQuestionIds === null
              ? 'This customer sees every active question (default).'
              : `This customer sees ${selectedQuestionIds.size} of ${allQuestions.length} questions.`}
          </p>

          <p className="muted" style={{ marginBottom: 4 }}>
            Filter by group (select one or more, then use the buttons below to assign all matching questions):
          </p>
          <div className="row" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
            {availableGroups.map((g) => {
              const active = groupFilter.has(g);
              return (
                <button
                  key={g}
                  type="button"
                  style={{ flex: 'none', opacity: active ? 1 : 0.6 }}
                  className={active ? 'primary' : undefined}
                  onClick={() => toggleGroupFilter(g)}
                >
                  {g}
                </button>
              );
            })}
          </div>

          <div className="row" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
            <button style={{ flex: 'none' }} onClick={handleSelectAllVisible}>
              {groupFilter.size === 0 ? 'Select all' : 'Select all in selected groups'}
            </button>
            <button style={{ flex: 'none' }} onClick={handleDeselectAllVisible}>
              {groupFilter.size === 0 ? 'Deselect all' : 'Deselect all in selected groups'}
            </button>
            <button style={{ flex: 'none' }} onClick={handleResetToAllQuestions} disabled={selectedQuestionIds === null && groupFilter.size === 0}>
              Reset to all questions
            </button>
            <button className="primary" style={{ flex: 'none' }} onClick={handleSaveAssignment} disabled={savingAssignment}>
              {savingAssignment ? 'Saving...' : 'Save assignment'}
            </button>
          </div>
          <div
            style={{
              maxHeight: 420,
              overflowY: 'auto',
              overflowX: 'hidden',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-card)',
            }}
          >
            {visibleQuestions.length === 0 && (
              <p className="muted" style={{ padding: 14 }}>
                No questions in the selected group(s).
              </p>
            )}
            {visibleQuestions
              .map((q) => {
                const checked = selectedQuestionIds === null || selectedQuestionIds.has(q.questionId);
                return (
                  <label
                    key={q.questionId}
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                      padding: '12px 14px',
                      borderBottom: '1px solid var(--color-border)',
                      fontWeight: 400,
                    }}
                  >
                    <input
                      type="checkbox"
                      style={{ width: 'auto', flex: 'none', marginTop: 3 }}
                      checked={checked}
                      onChange={() => toggleQuestion(q.questionId)}
                    />
                    <span style={{ flex: 1, minWidth: 0, overflowWrap: 'break-word', color: 'var(--color-text)' }}>
                      {q.text}
                      <br />
                      <span className="muted">
                        {q.group ?? 'AWS Migration'} · {q.category} · {q.priorityGroup}
                      </span>
                    </span>
                  </label>
                );
              })}
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="card">
          <h2>Questionnaire link</h2>
          {publicUrl && (
            <div className="success-banner">
              Link created. Copy it now - it will not be shown again: <br />
              <code>{publicUrl}</code>
            </div>
          )}
          <div className="row" style={{ alignItems: 'flex-end' }}>
            <div className="field" style={{ maxWidth: 160 }}>
              <label htmlFor="days">Valid for (days)</label>
              <input
                id="days"
                type="number"
                min={1}
                max={90}
                value={newLinkDays}
                onChange={(e) => setNewLinkDays(Number(e.target.value))}
              />
            </div>
            <button className="primary" style={{ flex: 'none' }} onClick={handleCreateLink}>
              Generate new link
            </button>
            <button className="danger" style={{ flex: 'none' }} onClick={handleRevokeLinks}>
              Revoke all links
            </button>
          </div>
          <table style={{ marginTop: 16 }}>
            <thead>
              <tr>
                <th>Created</th>
                <th>Expires</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {links.map((l) => (
                <tr key={l.createdAt}>
                  <td>{new Date(l.createdAt).toLocaleString()}</td>
                  <td>{new Date(l.expiresAt).toLocaleString()}</td>
                  <td>{l.revoked ? 'Revoked' : l.active ? 'Active' : 'Expired'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card">
        <h2>Answer history</h2>
        {history.length === 0 && <p className="muted">No submissions yet.</p>}
        {history.map((q) => {
          const latest = q.entries[q.entries.length - 1];
          const expanded = expandedHistory.has(q.questionId);
          return (
            <div key={q.questionId} style={{ borderBottom: '1px solid var(--color-border)', padding: '12px 0' }}>
              <div className="row" style={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
                <div>
                  <strong>{q.text}</strong>
                  <span className="muted"> ({q.category})</span>
                </div>
                {q.entries.length > 1 && (
                  <button onClick={() => toggleHistory(q.questionId)} style={{ flex: 'none' }}>
                    {expanded ? 'Hide history' : `History (${q.entries.length})`}
                  </button>
                )}
              </div>
              <p>
                {formatAnswer(latest?.value)}
                {latest?.changed && q.entries.length > 1 && <span className="badge changed">changed</span>}
              </p>
              {expanded && (
                <ul>
                  {q.entries.map((entry) => (
                    <li key={entry.version}>
                      v{entry.version} ({new Date(entry.submittedAt).toLocaleString()}
                      {entry.isDraft ? ', draft' : ''}): {formatAnswer(entry.value)}
                      {entry.changed && <span className="badge changed">changed</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Downloads an admin export (CSV or Excel) via fetch+blob rather than a
 * plain link, since these endpoints require the admin bearer token and a
 * plain <a href> navigation cannot attach an authorization header.
 */
async function downloadExport(url: string, accessToken: string | undefined, filename: string): Promise<void> {
  const res = await fetch(url, { headers: { authorization: `Bearer ${accessToken ?? ''}` } });
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(objectUrl);
}

function formatAnswer(value: unknown): string {
  if (value === undefined || value === null || value === '') return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}
